import React, {useState, useEffect, useMemo} from "react";
import {Modal, Button, Form} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import Select from "react-select";
import {dashboardAPI} from "../../../services/api";
import {mockStores} from "../../../data/stores";

export const EditStoreGroup = ({ show, storeGroup, onClose, onSave }: any) => {
    const {t} = useTranslation();
    const [formState, setFormState] = useState({
        name: '',
        description: '',
        active: true
    });
    const [stores, setStores] = useState<any[]>([]);
    const [selectedStores, setSelectedStores] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Check if demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        if (show && storeGroup) {
            // Populate form with store group data
            setFormState({
                name: storeGroup.name || '',
                description: storeGroup.description || '',
                active: storeGroup.active !== undefined ? storeGroup.active : true
            });
            setError(null);
            
            // Fetch stores
            setIsLoading(true);
            if (isDemoUser) {
                // Use mock data for demo
                setStores(mockStores);
                
                // Set selected stores if store group has stores
                if (storeGroup.stores && storeGroup.stores.length > 0) {
                    const selected = storeGroup.stores
                        .map((s: any) => ({
                            value: s.id || s,
                            label: typeof s === 'string' ? s : `${s.name || s.id}${s.city ? ` (${s.city})` : ''}`
                        }));
                    setSelectedStores(selected);
                } else {
                    setSelectedStores([]);
                }
                setIsLoading(false);
            } else {
                // Fetch stores from API
                dashboardAPI.getStores().then((response: any) => {
                    const storesData = Array.isArray(response) ? response : (response?.data || []);
                    setStores(storesData);
                    
                    // Fetch store group details to get assigned stores
                    dashboardAPI.getStoreGroupById(storeGroup.id).then((groupResponse: any) => {
                        // Handle both AxiosResponse and plain object responses
                        const groupData = groupResponse?.data || groupResponse;
                        if (groupData?.stores && groupData.stores.length > 0) {
                            const selected = groupData.stores
                                .map((s: any) => ({
                                    value: s.id || s,
                                    label: `${s.name || s.id}${s.city ? ` (${s.city})` : ''}`
                                }));
                            setSelectedStores(selected);
                        } else {
                            setSelectedStores([]);
                        }
                        setIsLoading(false);
                    }).catch((err: any) => {
                        console.error('Failed to fetch store group details:', err);
                        setIsLoading(false);
                    });
                }).catch((err: any) => {
                    console.error('Failed to fetch stores:', err);
                    setError(err?.message || 'Failed to fetch stores');
                    setIsLoading(false);
                });
            }
        }
    }, [show, storeGroup, isDemoUser]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setFormState(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async () => {
        if (!storeGroup) return;

        // Validation
        setError(null);
        
        if (!formState.name.trim()) {
            setError(t('StoreGroupList.name_required') || 'Store group name is required');
            return;
        }

        if (formState.name.trim().length < 2) {
            setError(t('StoreGroupList.name_min_length') || 'Store group name must be at least 2 characters');
            return;
        }

        if (formState.name.trim().length > 100) {
            setError(t('StoreGroupList.name_max_length') || 'Store group name must not exceed 100 characters');
            return;
        }

        if (formState.description && formState.description.length > 500) {
            setError(t('StoreGroupList.description_max_length') || 'Description must not exceed 500 characters');
            return;
        }

        setIsSubmitting(true);

        try {
            // Update store group basic info
            const storeGroupData = {
                name: formState.name.trim(),
                description: formState.description.trim() || undefined,
                active: formState.active
            };
            
            await dashboardAPI.updateStoreGroup(storeGroup.id, storeGroupData);
            
            // Update assigned stores
            const selectedStoreIds = selectedStores.map(s => s.value);
            if (isDemoUser) {
                // In demo mode, just log
                console.log('🔧 Demo: Would assign stores to store group:', selectedStoreIds);
            } else {
                // Get current stores in the group
                const groupDetails: any = await dashboardAPI.getStoreGroupById(storeGroup.id);
                // Handle both AxiosResponse and plain object responses
                const groupData = groupDetails?.data || groupDetails;
                const currentStores = groupData?.stores || [];
                const currentStoreIds = currentStores.map((s: any) => s.id || s);
                
                // Find stores to add and remove
                const storesToAdd = selectedStoreIds.filter(id => !currentStoreIds.includes(id));
                const storesToRemove = currentStoreIds.filter((id: string) => !selectedStoreIds.includes(id));
                
                // Add new stores
                if (storesToAdd.length > 0) {
                    await dashboardAPI.assignStoresToStoreGroup(storeGroup.id, storesToAdd);
                }
                
                // Remove stores
                if (storesToRemove.length > 0) {
                    await dashboardAPI.removeStoresFromStoreGroup(storeGroup.id, storesToRemove);
                }
            }
            
            await onSave();
        } catch (error: any) {
            console.error('Failed to update store group:', error);
            setError(error?.response?.data?.message || error?.message || t('StoreGroupList.update_failed') || 'Failed to update store group');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!storeGroup) return null;

    return (
        <Modal show={show} onHide={onClose} backdrop="static" size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t('StoreGroupList.edit_store_group') || 'Edit Store Group'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            {t('StoreGroupList.name') || 'Name'} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            value={formState.name}
                            onChange={handleChange}
                            placeholder={t('StoreGroupList.name_placeholder') || 'Enter store group name'}
                            disabled={isSubmitting}
                            maxLength={100}
                        />
                        <Form.Text className="text-muted">
                            {t('StoreGroupList.name_help') || 'Store group name must be unique within your company (2-100 characters)'}
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            {t('StoreGroupList.description') || 'Description'}
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="description"
                            value={formState.description}
                            onChange={handleChange}
                            placeholder={t('StoreGroupList.description_placeholder') || 'Enter description (optional)'}
                            disabled={isSubmitting}
                            maxLength={500}
                        />
                        <Form.Text className="text-muted">
                            {t('StoreGroupList.description_help') || 'Optional description (max 500 characters)'}
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            name="active"
                            checked={formState.active}
                            onChange={handleChange}
                            label={t('StoreGroupList.active') || 'Active'}
                            disabled={isSubmitting}
                        />
                        <Form.Text className="text-muted">
                            {t('StoreGroupList.active_help') || 'Inactive store groups cannot be assigned to users'}
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            {t('StoreGroupList.assign_stores') || 'Assign Stores'} <span className="text-danger">*</span>
                        </Form.Label>
                        {isLoading ? (
                            <div className="text-center py-3">
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <Select
                                isMulti
                                options={stores.map((store: any) => ({
                                    value: store.id,
                                    label: `${store.name || store.id}${store.city ? ` (${store.city})` : ''}`
                                }))}
                                value={selectedStores}
                                onChange={(selected: any) => setSelectedStores(selected || [])}
                                placeholder={t('StoreGroupList.select_stores_placeholder') || 'Select stores to assign to this group...'}
                                isDisabled={isSubmitting}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        )}
                        <Form.Text className="text-muted">
                            {t('StoreGroupList.assign_stores_help') || 'Select one or more stores to assign to this store group'}
                        </Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                    {t('Common.cancel') || 'Cancel'}
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {t('Common.updating') || 'Updating...'}
                        </>
                    ) : (
                        t('Common.update') || 'Update'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

